import React, { useState, useRef, useEffect } from "react";
import { useShallow } from "zustand/react/shallow";

import { Button, Checkbox, FormControl, FormControlLabel, FormGroup, Input, MenuItem, Radio, RadioGroup, Select } from "@mui/material";
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from "@mui/material";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode, { type Code } from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";
import PopupCommon from "@components/popup/popup_common";

import { useAuthStore } from "@user/store/auth_store";

import SearchPnPopup from "@user/views/popup/popup_search_pn";
import BackupStockInfoPopup from "@user/views/inoutbound/popup_backup_stock_info";

import searchIcon from "@user/assets/images/common/search.svg";

interface State {
  isShowSearchPn: boolean,
  isHoverBtn: boolean,
  workReqInfos: any[],
  isShowBackupStockInfo: boolean
}

const defState = {
  isShowSearchPn: false,
  isHoverBtn: false,
  workReqInfos: [],
  isShowBackupStockInfo: false
};

interface Info {
  workStationId: string,
  mngType: string,
  workReqEtcType: string,
  inoutType: string,
  wh: string,
  pnNo: string,
  pn: string,
  trSn: string,
  trNoInfos: Code[],
  trNo: string,
  trNoNewYn: string,
  snInfos: Code[],
  sn: string,
  snNewYn: string,
  expireDate: string,
  kwd: string,
  ctg: string,
  uom: string,
  reqQty: string,
  stockQty: string,
  pnRemark: string,
  choiceSkuType: string,
  choiceSkuCallQty: string,
  choiceSkuStockQty: string,
  workInfos: any[],
  skuStockInfos: any[]
}

const defInfo = {
  workStationId: "",
  mngType: "B",
  workReqEtcType: "B",
  inoutType: "I",
  wh: "",
  pnNo: "",
  pn: "",
  trSn: "",
  trNoInfos: [],
  trNo: "",
  trNoNewYn: "N",
  snInfos: [],
  sn: "",
  snNewYn: "N",
  expireDate: "",
  kwd: "",
  ctg: "",
  uom: "",
  reqQty: "",
  stockQty: "",
  pnRemark: "",
  choiceSkuType: "",
  choiceSkuCallQty: "",
  choiceSkuStockQty: "",
  workInfos: [],
  skuStockInfos: []
};

const validationMsg = [
  {id: "inoutType", type: "NotBlank", msg: "작업구분을 선택해 주세요."},
  {id: "wh", type: "NotBlank", msg: "창고를 선택해 주세요."},
  {id: "pnNo", type: "NotBlank", msg: "P/N을 선택해 주세요."},
  {id: "pn", type: "NotBlank", msg: "P/N을 선택해 주세요."},
  {id: "trSn", type: "NotBlank", msg: "TR No, S/N를 선택해 주세요."},
  {id: "reqQty", type: "NotNull", msg: "지시수량을 입력해 주세요."},
  {id: "reqQty", type: "Min", msg: "지시수량을 확인해 주세요."},
  {id: "reqQty", type: "Max", msg: "지시수량을 확인해 주세요."},
  {id: "workInfos", type: "NotFound", msg: "선택SKU가 있는 작업만 작업지시할 수 있습니다."},
  {id: "skuNo", type: "InvalidStatus", msg: "현재 작업대에서 호출할 수 없는 SKU입니다."}
];

const validationMsg4searchPn = [
  {id: "pn", type: "NotFound", msg: "PN 정보가 없습니다."}
];

function PopupBackupwork(props: any) {
  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [info, setInfo] = useState<Info>(kutil.deepCopy(defInfo));

  useEffect(() => {
    init();
  }, []);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await searchInfo();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
    info.wh = (commonCode.wh as any[]).filter(c => c.code === "B")?.[0]?.code ?? "";

    setInfo(prev => ({ ...prev, wh: info.wh, workStationId: props.data.workStationId }));
  }

  async function searchInfo() {
  }
 
  async function closePopup() {
    props.onClose();
  }

  async function checkPn() {
    if (!info.wh) {
      kutil.alert("창고를 선택해 주세요.");
      return;
    }

    if (info.pn) {
      searchPn();
    } else {
      openPopupSearchPn()
    }
  }

  async function searchPn(pn: string = info.pn) {
    try {
        const data = await kutil.sendGet("/api/inoutbound/backup_work.data?mode=searchPn", {mngType: info.mngType, wh: info.wh, pn: pn}, {
          validationMsg: validationMsg4searchPn
        });
        (data.trNoInfos as any[]).forEach((item) => {
          item.code = item.trNo;
          item.value = item.trNo;
          item.stockQty= item.stockQty;
        });
        (data.snInfos as any[]).forEach((item) => {
          item.code = item.sn;
          item.value = item.sn;
          item.stockQty= item.stockQty;
        });
        setInfo(prev => ({ ...prev, pnNo: data.pnInfo.pnNo, pn: pn, trNoInfos: data.trNoInfos, snInfos: data.snInfos, kwd: data.pnInfo.kwd, ctg: data.pnInfo.ctg, uom: data.pnInfo.uom, pnRemark: data.pnInfo.remark }));
    } catch(e) {
      console.log(e);
    }
  }

  async function changeTrNo(e: any) {
    const selTrNo = (info.trNoInfos as any[]).find(item => item.code === e.target.value);
    const stockQty = selTrNo ? selTrNo.stockQty : "";
    const expireDate = selTrNo ? selTrNo.expireDate : "";
    setInfo(prev => ({ ...prev, trNo: e.target.value, stockQty: stockQty, expireDate: expireDate, sn: "" }));
  }

  async function changeSn(e: any) {
    const selSn = (info.snInfos as any[]).find(item => item.code === e.target.value);
    const stockQty = selSn ? selSn.stockQty : "";
    const expireDate = selSn ? selSn.expireDate : "";
    setInfo(prev => ({ ...prev, sn: e.target.value, stockQty: stockQty, expireDate: expireDate, trNo: "" }));
  }

  async function save() {
    info.trSn = (info.trNo || "") + (info.sn || "");
    const isValid = await kutil.checkValidation(info, validationMsg);
    if (isValid == false) {
      return;
    }

    if (info.inoutType == "O" && Number(info.reqQty) > Number(info.stockQty)) {
      kutil.alert("재고수량을 초과하였습니다.");
      return;
    }

    if (await kutil.confirm("작업지시하시겠습니까?") == false) {
      return false;
    }

    const multiInfos: any[] = [];
    try {
      const data = await kutil.sendGet("/api/inoutbound/backup_work.data?mode=searchSkuStock", { mngType: info.mngType, wh: info.wh, pn: info.pn, trNo: info.trNo, sn: info.sn, workStationId: info.workStationId }, {});

      if (data.infos.length === 0) {
        if (info.inoutType == "O") {
          await kutil.alert("SKU가 없습니다.");
          return;
        }

        await kutil.sendPost("/api/inoutbound/backup_work.proc?mode=reqWork", info, { validationMsg: validationMsg });
        await kutil.alert("작업지시하였습니다.\r\n기존 재고가 없어 범위지정을 통해 SKU 추가가 필요합니다.");
        props.onClose({ isSuccess: true });
        return;
      }

      if (!data.infos.some((row: any) => row.outPossYn == "Y")) {
        await kutil.sendPost("/api/inoutbound/backup_work.proc?mode=reqWork", info, { validationMsg: validationMsg });
        await kutil.alert("작업지시하였습니다.\r\n현재 작업대에서 호출 가능한 SKU가 없어 BackUp 작업 목록에서 작업지시가 필요합니다.");
        props.onClose({ isSuccess: true });
        return;
      }

      if (data.infos.length === 1) {
        if (info.inoutType == "O" && Number(info.reqQty) > Number(data.infos[0].stockQty)) {
          await kutil.alert("재고수량을 초과하였습니다.");
          return;
        }

        info.choiceSkuType = "S";
        info.choiceSkuCallQty = "1";
        info.choiceSkuStockQty = "1";
        info.workInfos = data.infos;
        info.workInfos[0].jobQty = info.reqQty;
        await kutil.sendPost("/api/inoutbound/backup_work.proc?mode=orderWork", info, { validationMsg: validationMsg });
        await kutil.alert("작업지시하였습니다.");
        props.onClose({ isSuccess: true });
        return;
      }

      const stockQty = data.infos.filter((row: any) => row.outPossYn == "Y").reduce((sum: number, row: any) => sum + (Number(row.stockQty) || 0), 0);
      if (info.inoutType === "O" && (stockQty === 0 || Number(info.reqQty) > stockQty)) {
        await kutil.alert("재고수량을 초과하였습니다.");
        return;
      }

      info.skuStockInfos = data.infos;
      multiInfos.push(info);
      openPopupBackupStockInfo(multiInfos);
    } catch(e) {
      console.log(e);
    }
  }

  async function openPopupSearchPn() {
    setState(prev => ({ ...prev, isShowSearchPn: true }));
  }

  async function closePopupSearchPn(data: any) {
    setState(prev => ({ ...prev, isShowSearchPn: false }));

    if (data?.isSuccess) {
      searchPn(data.pn);
    }
  }

  async function openPopupBackupStockInfo(infos: any[]) {
    setState(prev => ({ ...prev, isShowBackupStockInfo: true, workReqInfos: infos}));
  }

  async function closePopupBackupStockInfo(data: any) {
    setState(prev => ({ ...prev, isShowBackupStockInfo: false }));

    if (data?.isSuccess) {
      props.onClose({isSuccess: true});
    }
  }

  return (
    <>
      {/* 팝업 */}
      <Dialog
        sx={{ "& .MuiDialog-container": { "& .MuiPaper-root": { width: "100%", maxWidth: "700px", }, }, }}
        aria-labelledby="customized-dialog-title"
        open={props.isShow}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          BackUp 작업 등록
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={closePopup}
          sx={(theme) => ({
            position: "absolute",
            right: 36,
            top: 24,
            color: theme.palette.grey[500],
          })}
        >
        </IconButton>
        <DialogContent>
          {/* 팝업 내용 */}
          <div className="popup-contents">
            {/* Cell 수정 */}
            <div className="table-wrap">
              <table>
                <colgroup>
                  <col style={{ width: "17%" }} />
                  <col style={{ width: "33%" }} />
                  <col style={{ width: "17%" }} />
                  <col style={{ width: "33%" }} />
                </colgroup>
                <tbody>
                  <tr>
                    <th scope="row">작업구분<em>*</em></th>
                    <td>
                      <div className="form-base">
                        <FormControl>
                          <RadioGroup
                            row
                            value={info.inoutType}
                            onChange={(e) => {setInfo(prev => ({ ...prev, inoutType: e.target.value, trNoNewYn: "N", snNewYn: "N" }))}}
                          >
                          {(commonCode.inoutType as any[]).map((code) => (
                            <FormControlLabel key={code.code} value={code.code} control={<Radio size="small" />} label={code.value} />
                          ))}
                          </RadioGroup>
                        </FormControl>
                      </div>
                    </td>
                    <th scope="row">창고<em>*</em></th>
                    <td>
                      <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                        <Select
                          value={info.wh}
                          onChange={(e) => {setInfo(prev => ({ ...prev, wh: e.target.value }))}}
                          displayEmpty
                        >
                          {(commonCode.wh as any[]).filter(c => c.code === "B").map((code) => (
                            <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">P/N<em>*</em></th>
                    <td>
                      <div className={state.isHoverBtn ? "input-btn on" : "input-btn"}>
                        <Input
                          inputProps={{ maxLength: 100 }}
                          value={info.pn}
                          onChange={(e) => { setInfo(prev => ({ ...prev, pn: e.target.value, pnNo: "", trNoInfos: [], trNo: "", snInfos: [], sn: "", kwd: "", ctg: "", uom: "", stockQty: "", pnRemark: "" })) }}
                          onKeyDown={(e) => { kutil.onKeyDownForInput(e, checkPn) }}
                        />
                        <button
                          onMouseEnter={() => setState(prev => ({ ...prev, isHoverBtn: true }))}
                          onMouseLeave={() => setState(prev => ({ ...prev, isHoverBtn: false }))}
                          onClick={checkPn}
                        >
                          <img src={searchIcon} alt="검색" />
                        </button>
                      </div>
                    </td>
                    <th scope="row">TR No</th>
                    <td>
                      {info.inoutType == "I" ?
                        (
                          <div className="form-div">
                          {info.trNoNewYn == "N" ?
                            (
                            <FormControl>
                              <Select
                                value={info.trNo}
                                onChange={changeTrNo}
                                displayEmpty
                              >
                                <MenuItem value="">선택</MenuItem>
                                {(info.trNoInfos as any[]).map((code) => (
                                  <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            ) : (
                            <Input
                              inputProps={{ maxLength: 100 }}
                              value={info.trNo}
                              onChange={(e) => {setInfo(prev => ({ ...prev, trNo: e.target.value }))}}
                              onKeyDown={(e) => {kutil.onKeyDownForInput(e, save)}}
                            />
                            )
                          }
                            <FormControlLabel control={<Checkbox size="small" />} label="신규" onChange={(e) => {setInfo(prev => ({ ...prev, trNoNewYn: ((e.target as HTMLInputElement).checked ? "Y" : "N") }))}} />
                          </div>
                        ) : (
                          <div className="form-div unit01">
                            <FormControl>
                              <Select
                                value={info.trNo}
                                onChange={changeTrNo}
                                displayEmpty
                              >
                                <MenuItem value="">선택</MenuItem>
                                {(info.trNoInfos as any[]).map((code) => (
                                  <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </div>
                        )
                      }
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">S/N</th>
                    <td>
                      {info.inoutType == "I" ?
                        (
                          <div className="form-div">
                          {info.snNewYn == "N" ?
                            (
                            <FormControl>
                              <Select
                                value={info.sn}
                                onChange={changeSn}
                                displayEmpty
                              >
                                <MenuItem value="">선택</MenuItem>
                                {(info.snInfos as any[]).map((code) => (
                                  <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            ) : (
                            <Input
                              inputProps={{ maxLength: 100 }}
                              value={info.sn}
                              onChange={(e) => {setInfo(prev => ({ ...prev, sn: e.target.value }))}}
                              onKeyDown={(e) => {kutil.onKeyDownForInput(e, save)}}
                            />
                            )
                          }
                            <FormControlLabel control={<Checkbox size="small" />} label="신규" onChange={(e) => {setInfo(prev => ({ ...prev, snNewYn: ((e.target as HTMLInputElement).checked ? "Y" : "N") }))}} />
                          </div>
                        ) : (
                          <div className="form-div unit01">
                            <FormControl>
                              <Select
                                value={info.sn}
                                onChange={changeSn}
                                displayEmpty
                              >
                                <MenuItem value="">선택</MenuItem>
                                {(info.snInfos as any[]).map((code) => (
                                  <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </div>
                        )
                      }
                    </td>
                    <th scope="row">KWD</th>
                    <td>
                      { info.kwd }
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">CTG</th>
                    <td>
                      { info.ctg }
                    </td>
                    <th scope="row">UOM</th>
                    <td>
                      { info.uom }
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">지시수량<em>*</em></th>
                    <td>
                      {info.inoutType == "I" ?
                        (
                          <div className="form-div unit01">
                            <Input
                              inputProps={{ maxLength: 7 }}
                              value={info.reqQty}
                              onChange={(e) => {setInfo(prev => ({ ...prev, reqQty: valueFormatter.number(valueFormatter.numberParser(e.target.value)) }))}}
                              onKeyDown={(e) => {kutil.onKeyDownForInput(e, save)}}
                            />
                          </div>
                        ) : (
                          <div className="form-div">
                            <Input
                              inputProps={{ maxLength: 7 }}
                              value={info.reqQty}
                              onChange={(e) => {setInfo(prev => ({ ...prev, reqQty: valueFormatter.number(valueFormatter.numberParser(e.target.value)) }))}}
                              onKeyDown={(e) => {kutil.onKeyDownForInput(e, save)}}
                            />
                            <span>/ { valueFormatter.number(info.stockQty) }</span>
                          </div>
                        )
                      }
                    </td>
                    <td colSpan={2}></td>
                  </tr>
                  <tr>
                    <th scope="col" colSpan={4} className="ta-center">Remark</th>
                  </tr>
                  <tr>
                    <td colSpan={4}>{ info.pnRemark }</td>
                  </tr>
                </tbody>
              </table>
            </div>
            {/*// Cell 수정 */}
            {state.isShowSearchPn &&
              <PopupCommon onClose={closePopupSearchPn}>
                <SearchPnPopup id={props.id} data={{mngType: info.mngType}} />
              </PopupCommon>
            }
            {state.isShowBackupStockInfo && <BackupStockInfoPopup isShow={state.isShowBackupStockInfo} onClose={closePopupBackupStockInfo} id={props.id} data={{multiYn:"N", infos: state.workReqInfos}} />}
          </div>
          {/*// 팝업 내용 */}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" autoFocus onClick={closePopup}>
            닫기
          </Button>
          { auth.write &&
          <Button variant="contained" autoFocus onClick={save}>
            작업지시
          </Button>
          }
        </DialogActions>
      </Dialog>
      {/*// 팝업 */}
    </>
  );
}

export default React.memo(PopupBackupwork);
